import { createPlugin } from "../createPlugin";
import { PlayerEvent } from "../../Core/PlayerEvents";
import { createLocalProgressStorage } from "../../Services/ProgressStorage";

/**
 * Resume playback — persists position via interchangeable storage.
 * Seeks only after media metadata is available (avoids seek-before-load races).
 */
export function createResumePlaybackPlugin(options) {
  options = options || {};
  var storage = options.storage || createLocalProgressStorage();
  var saveIntervalMs = options.saveIntervalMs != null ? options.saveIntervalMs : 5000;
  var completionThreshold =
    options.completionThreshold != null ? options.completionThreshold : 0.95;
  var minResumeSeconds = options.minResumeSeconds != null ? options.minResumeSeconds : 5;
  var autoResume = options.autoResume !== false;

  function mediaReady(ctx) {
    var video =
      ctx.engine && typeof ctx.engine.getVideoElement === "function"
        ? ctx.engine.getVideoElement()
        : null;
    if (video && video.readyState >= 1) {
      return true;
    }
    var st = ctx.getState ? ctx.getState() : ctx.engine.getState();
    return !!(st && st.duration > 0);
  }

  function applyPendingResume(ctx) {
    if (!ctx._resume || ctx._resume.pendingPosition == null || ctx._resume.offered) {
      return;
    }
    if (!mediaReady(ctx)) {
      return;
    }
    var pos = ctx._resume.pendingPosition;
    ctx._resume.pendingPosition = null;
    ctx._resume.offered = true;
    ctx.bus.emit(PlayerEvent.RESUME_OFFERED, {
      position: pos,
      duration: (ctx.getState() && ctx.getState().duration) || 0,
      storage: storage.getName(),
    });
    if (autoResume) {
      ctx.engine.seek(pos);
      ctx.bus.emit(PlayerEvent.RESUME_APPLIED, { position: pos });
      ctx.bus.emit(PlayerEvent.ANNOUNCEMENT, {
        message: "Resumed from " + Math.floor(pos) + " seconds",
      });
      // UMXP: keep session + timeline authoritative
      if (typeof options.getStateService === "function") {
        try {
          var svc = options.getStateService();
          if (svc && svc.getSession) {
            svc.getSession().update("resume", {
              position: pos,
              source: "local",
            });
          }
          var tl = svc && svc.getTimeline && svc.getTimeline();
          if (tl && tl.addMarker) {
            tl.addMarker({
              id: "resume",
              type: "resume_position",
              start: pos,
              meta: { source: "local" },
            });
          }
        } catch (e) {
          /* isolate */
        }
      }
    }
  }

  return createPlugin({
    name: "resume-playback",
    version: "0.1.1",
    onRegister: function (ctx) {
      ctx._resume = {
        timer: null,
        contentId: null,
        offered: false,
        pendingPosition: null,
        engineUnsubs: [],
      };
    },
    onReady: function (ctx) {
      // Guard: PluginHost must keep a stable ctx, but re-init if a host
      // ever supplies a fresh object (defensive for remount / register order).
      if (!ctx._resume) {
        ctx._resume = {
          timer: null,
          contentId: null,
          offered: false,
          pendingPosition: null,
          engineUnsubs: [],
        };
      }
      var contentId =
        options.contentId ||
        ctx.config.contentId ||
        (ctx.config.source && ctx.config.source.src) ||
        null;
      if (!contentId) {
        return;
      }
      ctx._resume.contentId = contentId;

      var save = function (completed) {
        var st = ctx.getState();
        var position = st.currentTime || 0;
        var duration = st.duration || 0;
        var record = {
          position: position,
          duration: duration,
          updatedAt: Date.now(),
          completed: !!completed,
        };
        return storage.save(contentId, record).then(function () {
          ctx.bus.emit(PlayerEvent.PROGRESS_SAVED, record);
        });
      };

      ctx._resume.save = save;

      // Apply resume once metadata / source is ready
      var tryApply = function () {
        applyPendingResume(ctx);
      };
      ctx._resume.engineUnsubs = [
        ctx.engine.on("durationchange", tryApply),
        ctx.engine.on("canplay", tryApply),
        ctx.engine.on("timeupdate", tryApply),
        ctx.engine.on(PlayerEvent.TIME_UPDATE, tryApply),
        ctx.engine.on(PlayerEvent.SOURCE_CHANGED, tryApply),
        ctx.engine.on(PlayerEvent.SEEK_COMPLETED, tryApply),
      ];

      storage.load(contentId).then(function (record) {
        if (!record || record.completed) {
          return;
        }
        var pos = record.position || 0;
        var dur = record.duration || 0;
        if (pos < minResumeSeconds) {
          return;
        }
        if (dur > 0 && pos / dur >= completionThreshold) {
          storage.clear(contentId);
          return;
        }
        ctx._resume.pendingPosition = pos;
        applyPendingResume(ctx);
      });

      if (ctx._resume.timer) {
        clearInterval(ctx._resume.timer);
      }
      ctx._resume.timer = setInterval(function () {
        var st = ctx.getState();
        if (st.playbackState === "playing") {
          save(false);
        }
      }, saveIntervalMs);

      ctx.bus.on("ui:resumeAccept", function () {
        storage.load(contentId).then(function (record) {
          if (record && record.position) {
            ctx.engine.seek(record.position);
            ctx.bus.emit(PlayerEvent.RESUME_APPLIED, {
              position: record.position,
            });
          }
        });
      });

      ctx.bus.on("ui:resumeDismiss", function () {
        storage.clear(contentId);
        ctx._resume.pendingPosition = null;
      });
    },
    onEvent: function (eventName, payload, ctx) {
      if (!ctx._resume || !ctx._resume.contentId) {
        return;
      }
      if (ctx._resume.pendingPosition != null) {
        if (
          eventName === PlayerEvent.SOURCE_CHANGED ||
          eventName === "durationchange" ||
          eventName === "canplay" ||
          eventName === "timeupdate" ||
          eventName === PlayerEvent.TIME_UPDATE ||
          eventName === PlayerEvent.SEEK_COMPLETED
        ) {
          applyPendingResume(ctx);
        }
      }
      if (!ctx._resume.save) {
        return;
      }
      if (eventName === "statechange") {
        var next = payload && payload.state;
        if (next === "paused") {
          ctx._resume.save(false);
        }
        if (next === "ended") {
          ctx._resume.save(true).then(function () {
            return storage.clear(ctx._resume.contentId);
          });
        }
      }
      if (eventName === "ended") {
        ctx._resume.save(true).then(function () {
          return storage.clear(ctx._resume.contentId);
        });
      }
    },
    onDestroy: function (ctx) {
      if (ctx._resume && ctx._resume.save && ctx._resume.contentId) {
        ctx._resume.save(false);
      }
      if (ctx._resume && ctx._resume.timer) {
        clearInterval(ctx._resume.timer);
      }
      if (ctx._resume && ctx._resume.engineUnsubs) {
        ctx._resume.engineUnsubs.forEach(function (u) {
          if (typeof u === "function") u();
        });
      }
    },
  });
}

export default createResumePlaybackPlugin;
